import React, { useState } from 'react';
import { NameEmailAvatar } from '../../../../../../../ui/components/Common/NameEmailAvatar';
import {
  WellnessAssessmentOverview,
  WellnessCandidateResult,
  WellnessDASS21Detail,
  WellnessGAD7Detail,
  WellnessPHQ9Detail,
  WellnessSummaryResult,
} from '../../../../../../../utils/types/wellness_types';
import {
  RISK_BADGE_CLASS,
  SEVERITY_BADGE_CLASS,
  SEVERITY_BAND_COLOR_CLASS,
} from '../../../Constants';

// ─── Static scoring data ───────────────────────────────────────────────────────

interface RangeSegment { label: string; flex: number; colorClass: string; }

const D21_DEP_RANGES: RangeSegment[] = [
  { label: 'Normal',       flex: 5,  colorClass: 'bg-emerald-400' },
  { label: 'Mild',         flex: 2,  colorClass: 'bg-yellow-400'  },
  { label: 'Moderate',     flex: 4,  colorClass: 'bg-amber-400'   },
  { label: 'Severe',       flex: 3,  colorClass: 'bg-red-500'     },
  { label: 'Ext. Severe',  flex: 8,  colorClass: 'bg-rose-700'    },
];
const D21_ANX_RANGES: RangeSegment[] = [
  { label: 'Normal',       flex: 4,  colorClass: 'bg-emerald-400' },
  { label: 'Mild',         flex: 1,  colorClass: 'bg-yellow-400'  },
  { label: 'Moderate',     flex: 3,  colorClass: 'bg-amber-400'   },
  { label: 'Severe',       flex: 2,  colorClass: 'bg-red-500'     },
  { label: 'Ext. Severe',  flex: 12, colorClass: 'bg-rose-700'    },
];
const D21_STR_RANGES: RangeSegment[] = [
  { label: 'Normal',       flex: 8,  colorClass: 'bg-emerald-400' },
  { label: 'Mild',         flex: 2,  colorClass: 'bg-yellow-400'  },
  { label: 'Moderate',     flex: 3,  colorClass: 'bg-amber-400'   },
  { label: 'Severe',       flex: 4,  colorClass: 'bg-red-500'     },
  { label: 'Ext. Severe',  flex: 5,  colorClass: 'bg-rose-700'    },
];
const GAD7_RANGES: RangeSegment[] = [
  { label: 'Minimal',  flex: 5,  colorClass: 'bg-emerald-400' },
  { label: 'Mild',     flex: 5,  colorClass: 'bg-yellow-400'  },
  { label: 'Moderate', flex: 5,  colorClass: 'bg-amber-400'   },
  { label: 'Severe',   flex: 7,  colorClass: 'bg-red-500'     },
];
const PHQ9_RANGES: RangeSegment[] = [
  { label: 'Minimal',       flex: 5,  colorClass: 'bg-emerald-400' },
  { label: 'Mild',          flex: 5,  colorClass: 'bg-yellow-400'  },
  { label: 'Moderate',      flex: 5,  colorClass: 'bg-amber-400'   },
  { label: 'Mod. Severe',   flex: 5,  colorClass: 'bg-red-500'     },
  { label: 'Severe',        flex: 8,  colorClass: 'bg-rose-700'    },
];

const DASS21_INTERPRETATION: Record<string, Record<string, string>> = {
  Depression: {
    Normal: 'No significant depression symptoms present.',
    Mild: 'Some low mood and reduced interest in activities.',
    Moderate: 'Notable dysphoria and some functional impairment.',
    Severe: 'High levels of dysphoria, low mood and lack of interest.',
    'Extremely Severe': 'Severe depression requiring urgent clinical attention.',
  },
  Anxiety: {
    Normal: 'No significant anxiety symptoms present.',
    Mild: 'Some tension and physical signs of anxiety.',
    Moderate: 'Significant anxiety with physiological symptoms.',
    Severe: 'High levels of tension, worry and physiological arousal.',
    'Extremely Severe': 'Extreme anxiety requiring urgent clinical attention.',
  },
  Stress: {
    Normal: 'Effective stress management, able to relax well.',
    Mild: 'Slightly elevated stress with difficulty winding down.',
    Moderate: 'Significant stress affecting daily functioning.',
    Severe: 'High levels of chronic stress and difficulty relaxing.',
    'Extremely Severe': 'Extremely high stress requiring immediate support.',
  },
};

const DASS21_RANGE_LABELS: Record<string, Record<string, string>> = {
  Depression: { Normal: '0–4', Mild: '5–6', Moderate: '7–10', Severe: '11–13', 'Extremely Severe': '14+' },
  Anxiety:    { Normal: '0–3', Mild: '4',   Moderate: '5–7',  Severe: '8–9',   'Extremely Severe': '10+' },
  Stress:     { Normal: '0–7', Mild: '8–9', Moderate: '10–12',Severe: '13–16', 'Extremely Severe': '17+' },
};

const RESPONSE_LABELS: Record<number, string> = {
  0: 'Not at all',
  1: 'Several days',
  2: 'More than half the days',
  3: 'Nearly every day',
};

const RESPONSE_DOT: Record<number, string> = {
  0: 'bg-emerald-400',
  1: 'bg-yellow-400',
  2: 'bg-amber-400',
  3: 'bg-red-500',
};

// ─── Shared icons ──────────────────────────────────────────────────────────────

const Dass21Icon = ({ cls = 'text-emerald-600' }) => (
  <svg className={`h-5 w-5 ${cls}`} fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={1.8}>
    <path strokeLinecap='round' strokeLinejoin='round' d='M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z' />
  </svg>
);
const Gad7Icon = ({ cls = 'text-blue-600' }) => (
  <svg className={`h-5 w-5 ${cls}`} fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={1.8}>
    <path strokeLinecap='round' strokeLinejoin='round' d='M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z' />
  </svg>
);
const Phq9Icon = ({ cls = 'text-purple-600' }) => (
  <svg className={`h-5 w-5 ${cls}`} fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={1.8}>
    <path strokeLinecap='round' strokeLinejoin='round' d='M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z' />
  </svg>
);

const DownloadIcon = () => (
  <svg className='h-4 w-4' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={2}>
    <path strokeLinecap='round' strokeLinejoin='round' d='M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3' />
  </svg>
);

const ChevronLeft = () => (
  <svg className='h-4 w-4' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={2}>
    <path strokeLinecap='round' strokeLinejoin='round' d='M15.75 19.5L8.25 12l7.5-7.5' />
  </svg>
);

const ChevronRight = () => (
  <svg className='h-4 w-4' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={2}>
    <path strokeLinecap='round' strokeLinejoin='round' d='M8.25 4.5l7.5 7.5-7.5 7.5' />
  </svg>
);

const ChevronDown = () => (
    <svg className='h-4 w-4' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={2}>
      <path strokeLinecap='round' strokeLinejoin='round' d='M19 9l-7 7-7-7' />
    </svg>
);

const CollapsibleSection: React.FC<{
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}> = ({ title, defaultOpen = false, children }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
      <div>
        <button
            type='button'
            onClick={() => setIsOpen((o) => !o)}
            className='flex items-center justify-between w-full text-left mb-2 group'
        >
          <p className='text-xs font-bold uppercase tracking-wide text-slate-500 group-hover:text-slate-700 transition-colors'>
            {title}
          </p>
          <span className={`text-slate-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}>
          <ChevronDown />
        </span>
        </button>
        {isOpen && children}
      </div>
  );
};

// ─── Assessment overview cards (Summary tab) ──────────────────────────────────

const ICON_BG: Record<string, string> = { DASS21: 'bg-emerald-50', PHQ9: 'bg-purple-50', GAD7: 'bg-blue-50' };
const ICONS: Record<string, React.ReactNode> = {
  DASS21: <Dass21Icon />, PHQ9: <Phq9Icon />, GAD7: <Gad7Icon />,
};

const OverviewCard: React.FC<{ data: WellnessAssessmentOverview }> = ({ data }) => {
  const total = data.bands.reduce((s, b) => s + b.count, 0);
  return (
      <div className='flex-1 min-w-[220px] rounded-xl border border-slate-200 bg-white p-5'>
        <div className='flex items-center gap-3 mb-4'>
          <div
              className={`flex h-9 w-9 items-center justify-center rounded-full ${ICON_BG[data.type] ?? 'bg-slate-50'}`}>
            {ICONS[data.type]}
          </div>
          <div>
            <p className='font-bold text-slate-900'>{data.displayName}</p>
            <p className='text-xs text-slate-500'>{data.description}</p>
          </div>
        </div>
        <div className='flex items-end gap-6 mb-4'>
          <div>
            <p className='text-3xl font-extrabold text-slate-900 leading-none'>{data.completed}</p>
            <p className='text-xs font-medium text-slate-500 mt-0.5'>Completed</p>
          </div>
          <div>
            <p className='text-3xl font-extrabold text-slate-400 leading-none'>{data.pending}</p>
            <p className='text-xs font-medium text-slate-400 mt-0.5'>Pending</p>
          </div>
        </div>
        <div className='flex h-2 w-full overflow-hidden rounded-full gap-0.5'>
          {total > 0 && data.bands.map((band) => (
              <div key={band.label}
                   className={`h-2 rounded-full ${SEVERITY_BAND_COLOR_CLASS[band.label] ?? 'bg-slate-300'}`}
                   style={{width: `${(band.count / total) * 100}%`}}/>
          ))}
        </div>
        <div className='mt-3 flex flex-wrap gap-x-3 gap-y-1'>
          {data.bands.map((band) => (
              <span key={band.label} className='inline-flex items-center gap-1 text-[11px] text-slate-600'>
            <span className={`h-2 w-2 rounded-full ${SEVERITY_BAND_COLOR_CLASS[band.label] ?? 'bg-slate-300'}`}/>
                {band.count} {band.label}
          </span>
          ))}
        </div>
      </div>
  );
};

const SkeletonOverviewCard: React.FC = () => (
    <div className='flex-1 min-w-[220px] rounded-xl border border-slate-200 bg-white p-5 animate-pulse'>
      <div className='flex items-center gap-3 mb-4'>
        <div className='h-9 w-9 rounded-full bg-slate-100'/>
        <div className='space-y-1.5'>
          <div className='h-3 w-20 rounded bg-slate-100'/>
          <div className='h-2.5 w-32 rounded bg-slate-100'/>
        </div>
      </div>
      <div className='flex gap-6 mb-4'>
        <div className='h-8 w-8 rounded bg-slate-100'/>
        <div className='h-8 w-8 rounded bg-slate-100'/>
      </div>
      <div className='h-2 w-full rounded-full bg-slate-100'/>
    </div>
);

// ─── Segmented score bar with position marker ──────────────────────────────────

const ScoreBar: React.FC<{ score: number; max: number; ranges: RangeSegment[] }> = ({ score, max, ranges }) => {
  const pct = Math.min(100, (score / max) * 100);
  return (
    <div className='relative pt-5 pb-1'>
      <div
          className='absolute top-0 text-slate-600 text-xs leading-none'
          style={{ left: `${pct}%`, transform: 'translateX(-50%)' }}
      >▼</div>
      <div className='flex h-3 w-full overflow-hidden rounded-full'>
        {ranges.map((r, i) => (
          <div key={i} className={`h-full ${r.colorClass}`} style={{ flex: r.flex }} />
        ))}
      </div>
      <div className='flex justify-between mt-1.5'>
        {ranges.map((r, i) => (
          <span key={i} className='text-[9px] text-slate-400 leading-none'>{r.label}</span>
        ))}
      </div>
    </div>
  );
};

// ─── Candidate results table (inside Summary page > Candidate Results tab) ──────

interface PaginationProps {
  currentPage: number; totalPages: number; totalRecords: number; onPageChange: (p: number) => void;
}

const ScoreCell: React.FC<{ score: number; max: number; severity: string }> = ({ score, max, severity }) => (
  <div>
    <span className='text-sm font-semibold text-slate-800'>{score}<span className='font-normal text-slate-400'>/{max}</span></span>
    <div className='mt-0.5'>
      <span className={`inline-block rounded px-1.5 py-0.5 text-[10px] font-semibold ${SEVERITY_BADGE_CLASS[severity] ?? 'bg-slate-100 text-slate-600'}`}>
        {severity}
      </span>
    </div>
  </div>
);

const TableSkeletonRows: React.FC<{ cols: number }> = ({ cols }) => (
  <>
    {Array.from({ length: 5 }).map((_, i) => (
      <tr key={i} className='border-b border-slate-100'>
        <td className='px-4 py-3'><div className='h-4 w-4 rounded bg-slate-100 animate-pulse' /></td>
        <td className='px-4 py-3'>
          <div className='flex items-center gap-2'>
            <div className='h-8 w-8 rounded-full bg-slate-100 animate-pulse' />
            <div className='space-y-1.5'><div className='h-3 w-24 rounded bg-slate-100 animate-pulse' /><div className='h-2.5 w-36 rounded bg-slate-100 animate-pulse' /></div>
          </div>
        </td>
        {Array.from({ length: cols - 2 }).map((__, j) => (
          <td key={j} className='px-4 py-3'><div className='h-3.5 w-16 rounded bg-slate-100 animate-pulse' /></td>
        ))}
      </tr>
    ))}
  </>
);

const CandidatesResultsTable: React.FC<{
  results: WellnessCandidateResult[];
  isLoading: boolean;
  pagination: PaginationProps;
  onView: (r: WellnessCandidateResult) => void;
}> = ({ results, isLoading, pagination, onView }) => {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const { currentPage, totalPages, totalRecords, onPageChange } = pagination;

  const toggleAll = () =>
    setSelected((p) => p.size === results.length ? new Set() : new Set(results.map((r) => r.candidateId)));
  const toggle = (id: string) =>
    setSelected((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const allSel = results.length > 0 && selected.size === results.length;

  return (
    <div className='space-y-3'>
      <div className='overflow-hidden rounded-xl border border-slate-200'>
        <table className='w-full text-sm table-fixed'>
          <colgroup>
            <col style={{ width: '40px' }} />
            <col style={{ width: '22%' }} />
            <col style={{ width: '12%' }} />
            <col style={{ width: '12%' }} />
            <col style={{ width: '12%' }} />
            <col style={{ width: '11%' }} />
            <col style={{ width: '10%' }} />
            <col style={{ width: '13%' }} />
            <col style={{ width: '60px' }} />
          </colgroup>
          <thead>
            <tr className='border-b border-slate-200 bg-slate-50'>
              <th className='px-4 py-3'>
                <input type='checkbox' checked={allSel} onChange={toggleAll} className='rounded border-slate-300 accent-emerald-600' />
              </th>
              {['CANDIDATE', 'DASS-21', 'PHQ-9', 'GAD-7', 'OVERALL RISK', 'STATUS', 'COMPLETED ON'].map((h) => (
                <th key={h} className='px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500'>{h}</th>
              ))}
              <th className='px-4 py-3' />
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <TableSkeletonRows cols={9} />
            ) : results.length === 0 ? (
              <tr><td colSpan={9} className='py-14 text-center text-sm text-slate-400'>No results yet</td></tr>
            ) : (
              results.map((r) => (
                <tr
                    key={r.candidateId}
                    className={`border-b border-slate-100 last:border-0 transition-colors ${
                      selected.has(r.candidateId) ? 'bg-emerald-50/40' : 'hover:bg-slate-50/60'
                    }`}
                >
                  <td className='px-4 py-3'>
                    <input type='checkbox' checked={selected.has(r.candidateId)} onChange={() => toggle(r.candidateId)} className='rounded border-slate-300 accent-emerald-600' />
                  </td>
                  <td className='px-4 py-3'>
                    <NameEmailAvatar name={r.name} email={r.email} avatar={undefined} avatarWidth='32px' avatarHeight='32px' nameFontSize='13px' emailFontSize='11px' />
                  </td>
                  <td className='px-4 py-3'>
                    {r.dass21 ? <ScoreCell score={r.dass21.totalScore} max={r.dass21.totalMax} severity={r.dass21.severity} /> : <span className='text-slate-300'>—</span>}
                  </td>
                  <td className='px-4 py-3'>
                    {r.phq9 ? <ScoreCell score={r.phq9.totalScore} max={r.phq9.totalMax} severity={r.phq9.severity} /> : <span className='text-slate-300'>—</span>}
                  </td>
                  <td className='px-4 py-3'>
                    {r.gad7 ? <ScoreCell score={r.gad7.totalScore} max={r.gad7.totalMax} severity={r.gad7.severity} /> : <span className='text-slate-300'>—</span>}
                  </td>
                  <td className='px-4 py-3'>
                    <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold ${RISK_BADGE_CLASS[r.overallRisk] ?? 'bg-slate-100 text-slate-600'}`}>
                      {r.overallRisk}
                    </span>
                  </td>
                  <td className='px-4 py-3 text-sm text-slate-600'>{r.status}</td>
                  <td className='px-4 py-3 text-sm text-slate-500'>{r.completedOn ?? '—'}</td>
                  <td className='px-4 py-3 text-right'>
                    <button
                        type='button'
                        onClick={() => onView(r)}
                        className='rounded-lg border border-slate-200 px-3 py-1 text-xs font-semibold text-emerald-700 hover:bg-emerald-50 hover:border-emerald-200 transition-colors'
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {!isLoading && totalRecords > 0 && (
        <div className='flex items-center justify-between text-xs text-slate-500'>
          <span>{totalRecords} result{totalRecords !== 1 ? 's' : ''}</span>
          {totalPages > 1 && (
            <div className='flex gap-1'>
              <button type='button' onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1} className='rounded-lg border border-slate-200 px-3 py-1 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed'>Prev</button>
              <span className='rounded-lg border border-slate-200 bg-white px-3 py-1'>{currentPage} / {totalPages}</span>
              <button type='button' onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages} className='rounded-lg border border-slate-200 px-3 py-1 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed'>Next</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── DASS-21 detail card ───────────────────────────────────────────────────────

const Dass21DetailCard: React.FC<{ d: WellnessDASS21Detail }> = ({ d }) => {
  const domains: Array<{ label: string; score: number; max: number; severity: string; ranges: RangeSegment[] }> = [
    { label: 'Depression', score: d.depressionScore, max: d.depressionMax, severity: d.depressionSeverity, ranges: D21_DEP_RANGES },
    { label: 'Anxiety',    score: d.anxietyScore,    max: d.anxietyMax,    severity: d.anxietySeverity,    ranges: D21_ANX_RANGES },
    { label: 'Stress',     score: d.stressScore,     max: d.stressMax,     severity: d.stressSeverity,     ranges: D21_STR_RANGES },
  ];

  return (
    <div className='rounded-xl border border-slate-200 bg-white p-5 space-y-4'>
      {/* Header */}
      <div className='flex items-center gap-3'>
        <div className='flex h-9 w-9 items-center justify-center rounded-full bg-emerald-50'>
          <Dass21Icon />
        </div>
        <div>
          <p className='font-bold text-slate-900'>DASS-21</p>
          <p className='text-xs text-slate-500'>Depression, Anxiety &amp; Stress Scale · 21 items</p>
        </div>
        <div className='ml-auto text-right'>
          <p className='text-xl font-extrabold text-slate-900 leading-none'>{d.totalScore}<span className='text-sm font-normal text-slate-400'>/{d.totalMax}</span></p>
          <span className={`inline-block rounded px-2 py-0.5 text-[11px] font-semibold mt-0.5 ${SEVERITY_BADGE_CLASS[d.severity] ?? 'bg-slate-100 text-slate-600'}`}>{d.severity}</span>
        </div>
      </div>

      {/* Domain score bars */}
      <div className='space-y-4'>
        {domains.map((dom) => (
          <div key={dom.label} className='rounded-lg border border-slate-100 bg-slate-50/50 p-3'>
            <div className='flex items-center justify-between mb-1'>
              <span className='text-sm font-semibold text-slate-700'>{dom.label}</span>
              <div className='flex items-center gap-2'>
                <span className='text-sm font-bold text-slate-900'>{dom.score}<span className='font-normal text-slate-400'>/{dom.max}</span></span>
                <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${SEVERITY_BADGE_CLASS[dom.severity] ?? 'bg-slate-100 text-slate-600'}`}>{dom.severity}</span>
              </div>
            </div>
            <ScoreBar score={dom.score} max={dom.max} ranges={dom.ranges} />
          </div>
        ))}
      </div>

      {/* Breakdown table */}
      <CollapsibleSection title='Score Breakdown' defaultOpen>
        <div className='overflow-hidden rounded-lg border border-slate-100'>
          <table className='w-full text-xs'>
            <thead>
            <tr className='bg-slate-50'>
              {['Domain', 'Score', 'Severity', 'Range', 'Interpretation'].map((h) => (
                  <th key={h} className='px-3 py-2 text-left font-semibold text-slate-500'>{h}</th>
              ))}
            </tr>
            </thead>
            <tbody>
            {domains.map((dom) => (
                <tr key={dom.label} className='border-t border-slate-100'>
                  <td className='px-3 py-2 font-medium text-slate-700'>{dom.label}</td>
                  <td className='px-3 py-2 text-slate-700'>{dom.score}/{dom.max}</td>
                  <td className='px-3 py-2'>
                    <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${SEVERITY_BADGE_CLASS[dom.severity] ?? ''}`}>{dom.severity}</span>
                  </td>
                  <td className='px-3 py-2 text-slate-500'>{DASS21_RANGE_LABELS[dom.label]?.[dom.severity] ?? '—'}</td>
                  <td className='px-3 py-2 text-slate-500 max-w-[200px]'>{DASS21_INTERPRETATION[dom.label]?.[dom.severity] ?? '—'}</td>
                </tr>
            ))}
            </tbody>
          </table>
        </div>
      </CollapsibleSection>

      {/* Per-question breakdown */}
      {d.questions.length > 0 && (
          <CollapsibleSection title='Question Responses'>
            <div className='overflow-hidden rounded-lg border border-slate-100'>
              <table className='w-full text-xs'>
                <thead>
                <tr className='bg-slate-50'>
                  {['#', 'Item', 'Domain', 'Score', 'Response'].map((h) => (
                      <th key={h} className='px-3 py-2 text-left font-semibold text-slate-500'>{h}</th>
                  ))}
                </tr>
                </thead>
                <tbody>
                {d.questions.map((q, i) => (
                    <tr key={q.questionId} className='border-t border-slate-100'>
                      <td className='px-3 py-2 text-slate-400'>{i + 1}</td>
                      <td className='px-3 py-2 text-slate-700 max-w-[200px]'>{q.text}</td>
                      <td className='px-3 py-2 text-slate-500'>{q.domain ?? '—'}</td>
                      <td className='px-3 py-2 font-bold text-slate-700'>{q.score}</td>
                      <td className='px-3 py-2'>
                      <span className='inline-flex items-center gap-1.5'>
                        <span className={`h-2 w-2 rounded-full ${RESPONSE_DOT[q.score] ?? 'bg-slate-300'}`} />
                        <span className='text-slate-600'>{RESPONSE_LABELS[q.score] ?? '—'}</span>
                      </span>
                      </td>
                    </tr>
                ))}
                </tbody>
              </table>
            </div>
          </CollapsibleSection>
      )}
    </div>
  );
};

// ─── GAD-7 detail card ────────────────────────────────────────────────────────

const Gad7DetailCard: React.FC<{ d: WellnessGAD7Detail }> = ({ d }) => (
  <div className='rounded-xl border border-slate-200 bg-white p-5 space-y-4'>
    <div className='flex items-center gap-3'>
      <div className='flex h-9 w-9 items-center justify-center rounded-full bg-blue-50'>
        <Gad7Icon />
      </div>
      <div>
        <p className='font-bold text-slate-900'>GAD-7</p>
        <p className='text-xs text-slate-500'>Generalised Anxiety Disorder · 7 items</p>
      </div>
      <div className='ml-auto text-right'>
        <p className='text-xl font-extrabold text-slate-900 leading-none'>{d.totalScore}<span className='text-sm font-normal text-slate-400'>/{d.totalMax}</span></p>
        <span className={`inline-block rounded px-2 py-0.5 text-[11px] font-semibold mt-0.5 ${SEVERITY_BADGE_CLASS[d.severity] ?? 'bg-slate-100 text-slate-600'}`}>{d.severity}</span>
      </div>
    </div>
    <div className='rounded-lg border border-slate-100 bg-slate-50/50 p-3'>
      <ScoreBar score={d.totalScore} max={d.totalMax} ranges={GAD7_RANGES} />
    </div>
    {d.questions.length > 0 && (
        <CollapsibleSection title='Item Breakdown'>
          <div className='overflow-hidden rounded-lg border border-slate-100'>
            <table className='w-full text-xs'>
              <thead>
              <tr className='bg-slate-50'>
                {['#', 'Item', 'Score', 'Response'].map((h) => (
                    <th key={h} className='px-3 py-2 text-left font-semibold text-slate-500'>{h}</th>
                ))}
              </tr>
              </thead>
              <tbody>
              {d.questions.map((q, i) => (
                  <tr key={q.questionId} className='border-t border-slate-100'>
                    <td className='px-3 py-2 text-slate-400'>{i + 1}</td>
                    <td className='px-3 py-2 text-slate-700 max-w-[220px]'>{q.text}</td>
                    <td className='px-3 py-2 font-bold text-slate-700'>{q.score}</td>
                    <td className='px-3 py-2'>
                    <span className='inline-flex items-center gap-1.5'>
                      <span className={`h-2 w-2 rounded-full ${RESPONSE_DOT[q.score] ?? 'bg-slate-300'}`} />
                      <span className='text-slate-600'>{RESPONSE_LABELS[q.score] ?? '—'}</span>
                    </span>
                    </td>
                  </tr>
              ))}
              </tbody>
            </table>
          </div>
        </CollapsibleSection>
    )}
  </div>
);

// ─── PHQ-9 detail card ────────────────────────────────────────────────────────

const Phq9DetailCard: React.FC<{ d: WellnessPHQ9Detail }> = ({ d }) => (
  <div className='rounded-xl border border-slate-200 bg-white p-5 space-y-4'>
    <div className='flex items-center gap-3'>
      <div className='flex h-9 w-9 items-center justify-center rounded-full bg-purple-50'>
        <Phq9Icon />
      </div>
      <div>
        <p className='font-bold text-slate-900'>PHQ-9</p>
        <p className='text-xs text-slate-500'>Patient Health Questionnaire · 9 items</p>
      </div>
      <div className='ml-auto text-right'>
        <p className='text-xl font-extrabold text-slate-900 leading-none'>{d.totalScore}<span className='text-sm font-normal text-slate-400'>/{d.totalMax}</span></p>
        <span className={`inline-block rounded px-2 py-0.5 text-[11px] font-semibold mt-0.5 ${SEVERITY_BADGE_CLASS[d.severity] ?? 'bg-slate-100 text-slate-600'}`}>{d.severity}</span>
      </div>
    </div>
    <div className='rounded-lg border border-slate-100 bg-slate-50/50 p-3'>
      <ScoreBar score={d.totalScore} max={d.totalMax} ranges={PHQ9_RANGES} />
    </div>
    {d.questions.length > 0 && (
        <CollapsibleSection title='Item Breakdown'>
          <div className='overflow-hidden rounded-lg border border-slate-100'>
            <table className='w-full text-xs'>
              <thead>
              <tr className='bg-slate-50'>
                {['#', 'Item', 'Score', 'Response'].map((h) => (
                    <th key={h} className='px-3 py-2 text-left font-semibold text-slate-500'>{h}</th>
                ))}
              </tr>
              </thead>
              <tbody>
              {d.questions.map((q, i) => (
                  <tr key={q.questionId} className='border-t border-slate-100'>
                    <td className='px-3 py-2 text-slate-400'>{i + 1}</td>
                    <td className='px-3 py-2 text-slate-700 max-w-[220px]'>{q.text}</td>
                    <td className='px-3 py-2 font-bold text-slate-700'>{q.score}</td>
                    <td className='px-3 py-2'>
                    <span className='inline-flex items-center gap-1.5'>
                      <span className={`h-2 w-2 rounded-full ${RESPONSE_DOT[q.score] ?? 'bg-slate-300'}`} />
                      <span className='text-slate-600'>{RESPONSE_LABELS[q.score] ?? '—'}</span>
                    </span>
                    </td>
                  </tr>
              ))}
              </tbody>
            </table>
          </div>
        </CollapsibleSection>
    )}
  </div>
);

// ─── Full candidate detail page (image 3 layout) ──────────────────────────────

const CandidateDetailPage: React.FC<{
  candidate: WellnessCandidateResult;
  allCandidates: WellnessCandidateResult[];
  onBack: () => void;
  onSelectCandidate: (c: WellnessCandidateResult) => void;
}> = ({ candidate, allCandidates, onBack, onSelectCandidate }) => {
  const [detailTab, setDetailTab] = useState<'summary' | 'results'>('results');
  const idx = allCandidates.findIndex((c) => c.candidateId === candidate.candidateId);

  const handlePrev = () => {
    if (idx > 0) onSelectCandidate(allCandidates[idx - 1]);
  };

  const handleNext = () => {
    if (idx < allCandidates.length - 1) onSelectCandidate(allCandidates[idx + 1]);
  };

  const assessmentsCompleted = [
    candidate.dass21 ? 'DASS-21' : null,
    candidate.gad7   ? 'GAD-7'   : null,
    candidate.phq9   ? 'PHQ-9'   : null,
  ].filter(Boolean);

  const downloadExport = () => {
    const lines = [
      `Candidate Results – ${candidate.name}`,
      `Email: ${candidate.email}`,
      `Overall Risk: ${candidate.overallRisk}`,
      `Completed: ${candidate.completedOn ?? '—'}`,
    ];
    const blob = new Blob([lines.join('\n')], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `${candidate.name.replace(/\s+/g, '_')}_results.txt`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className='space-y-5'>

      {/* Breadcrumb */}
      <nav className='flex items-center gap-1.5 text-xs text-slate-400'>
        <button type='button' onClick={onBack} className='hover:text-emerald-600 transition-colors'>Wellness</button>
        <span>›</span>
        <button type='button' onClick={onBack} className='hover:text-emerald-600 transition-colors'>Screening Results</button>
        <span>›</span>
        <span className='font-semibold text-slate-600'>Candidate Results</span>
      </nav>

      {/* Title + candidate nav */}
      <div className='flex items-start justify-between gap-4'>
        <div>
          <button
              type='button'
              onClick={onBack}
              className='mb-2 inline-flex items-center gap-1 text-xs text-slate-500 hover:text-emerald-600 transition-colors'
          >
            <ChevronLeft/> Back to results
          </button>
          <h2 className='text-xl font-extrabold text-slate-900'>Candidate Results</h2>
          <p className='text-sm text-slate-500'>Individual wellness screening report</p>
        </div>
        <div className='flex items-center gap-3'>
          <button
              type='button'
              onClick={handlePrev}
              disabled={idx <= 0}
              className='rounded-lg border border-slate-200 p-1.5 text-slate-500 hover:bg-slate-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors'
          >
            <ChevronLeft/>
          </button>
          <div className='flex items-center gap-2.5'>
            <NameEmailAvatar name={candidate.name} email={candidate.email} avatar={undefined}
                             avatarWidth='36px' avatarHeight='36px' nameFontSize='13px' emailFontSize='11px'/>
          </div>
          <button
              type='button'
              onClick={handleNext}
              disabled={idx >= allCandidates.length - 1}
              className='rounded-lg border border-slate-200 p-1.5 text-slate-500 hover:bg-slate-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors'
          >
            <ChevronRight/>
          </button>
        </div>
      </div>

      {/* Meta row */}
      <div className='grid grid-cols-2 gap-3 sm:grid-cols-4'>
        <div className='rounded-xl border border-slate-200 bg-white px-4 py-3'>
          <p className='text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-1'>Completion Date</p>
          <p className='text-sm font-bold text-slate-800'>{candidate.completedOn ?? 'Not completed'}</p>
        </div>
        <div className='rounded-xl border border-slate-200 bg-white px-4 py-3'>
          <p className='text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-1'>Assessments Completed</p>
          <div className='flex flex-wrap gap-1'>
            {assessmentsCompleted.length === 0 ? (
                <span className='text-sm text-slate-400'>None</span>
            ) : assessmentsCompleted.map((a) => (
                <span key={a!} className='inline-flex items-center gap-1 text-xs font-semibold text-slate-700'>
                <span className='h-1.5 w-1.5 rounded-full bg-emerald-500'/>{a}
              </span>
            ))}
          </div>
        </div>
        <div className='rounded-xl border border-slate-200 bg-white px-4 py-3'>
          <p className='text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-1'>Overall Wellness Risk</p>
          <span className={`inline-block rounded-full px-3 py-0.5 text-sm font-bold ${RISK_BADGE_CLASS[candidate.overallRisk] ?? 'bg-slate-100 text-slate-600'}`}>
            {candidate.overallRisk} Risk
          </span>
        </div>
        <div className='rounded-xl border border-slate-200 bg-white px-4 py-3'>
          <p className='text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-1'>Interpretation Guide</p>
          <p className='text-xs text-slate-600'>Scores reflect self-reported symptom severity at time of assessment.</p>
        </div>
      </div>

      {/* Sub-tabs: Summary | Candidate Results */}
      <div className='flex gap-1 border-b border-slate-100'>
        {(['summary', 'results'] as const).map((t) => (
          <button
              key={t}
              type='button'
              onClick={() => setDetailTab(t)}
              className={`relative px-4 py-2 text-sm font-semibold transition-colors whitespace-nowrap capitalize ${
                detailTab === t ? 'text-emerald-700' : 'text-slate-500 hover:text-slate-700'
              }`}
          >
            {t === 'summary' ? 'Summary' : 'Candidate Results'}
            {detailTab === t && <span className='absolute bottom-0 left-0 right-0 h-[2px] bg-emerald-600 rounded-t-full' />}
          </button>
        ))}
      </div>

      {/* Summary sub-tab */}
      {detailTab === 'summary' && (
        <div className='rounded-xl border border-slate-200 bg-white p-5'>
          <p className='text-sm text-slate-500'>Summary view shows the overall wellness picture for this candidate.</p>
          <div className='mt-4 grid grid-cols-3 gap-4'>
            {candidate.dass21 && (
              <div className='rounded-lg border border-slate-100 bg-slate-50 p-3 text-center'>
                <p className='text-xs text-slate-500 mb-1'>DASS-21</p>
                <p className='text-2xl font-extrabold text-slate-900'>{candidate.dass21.totalScore}<span className='text-sm font-normal text-slate-400'>/{candidate.dass21.totalMax}</span></p>
                <span className={`inline-block mt-1 rounded px-2 py-0.5 text-[11px] font-semibold ${SEVERITY_BADGE_CLASS[candidate.dass21.severity] ?? ''}`}>{candidate.dass21.severity}</span>
              </div>
            )}
            {candidate.gad7 && (
              <div className='rounded-lg border border-slate-100 bg-slate-50 p-3 text-center'>
                <p className='text-xs text-slate-500 mb-1'>GAD-7</p>
                <p className='text-2xl font-extrabold text-slate-900'>{candidate.gad7.totalScore}<span className='text-sm font-normal text-slate-400'>/{candidate.gad7.totalMax}</span></p>
                <span className={`inline-block mt-1 rounded px-2 py-0.5 text-[11px] font-semibold ${SEVERITY_BADGE_CLASS[candidate.gad7.severity] ?? ''}`}>{candidate.gad7.severity}</span>
              </div>
            )}
            {candidate.phq9 && (
              <div className='rounded-lg border border-slate-100 bg-slate-50 p-3 text-center'>
                <p className='text-xs text-slate-500 mb-1'>PHQ-9</p>
                <p className='text-2xl font-extrabold text-slate-900'>{candidate.phq9.totalScore}<span className='text-sm font-normal text-slate-400'>/{candidate.phq9.totalMax}</span></p>
                <span className={`inline-block mt-1 rounded px-2 py-0.5 text-[11px] font-semibold ${SEVERITY_BADGE_CLASS[candidate.phq9.severity] ?? ''}`}>{candidate.phq9.severity}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Candidate Results sub-tab */}
      {detailTab === 'results' && (
        <div className='space-y-4'>
          {/* 3 assessment cards */}
          <div className='grid grid-cols-1 gap-4 xl:grid-cols-3'>
            {candidate.dass21 && <Dass21DetailCard d={candidate.dass21} />}
            {candidate.gad7   && <Gad7DetailCard   d={candidate.gad7}   />}
            {candidate.phq9   && <Phq9DetailCard   d={candidate.phq9}   />}
          </div>

          {/* Bottom: Important Notes + Recommended Actions + Export */}
          <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
            <div className='rounded-xl border border-slate-200 bg-amber-50/60 p-5'>
              <p className='mb-2 text-sm font-bold text-amber-800'>Important Notes</p>
              <ul className='space-y-1.5 text-xs text-amber-700 list-disc list-inside'>
                <li>This assessment is self-reported and should not replace clinical evaluation.</li>
                <li>Results should be reviewed by a qualified mental health professional.</li>
                <li>Scores may vary based on the candidate&apos;s state at the time of completion.</li>
                <li>All data is handled in accordance with privacy regulations.</li>
              </ul>
            </div>
            <div className='rounded-xl border border-slate-200 bg-blue-50/60 p-5'>
              <p className='mb-2 text-sm font-bold text-blue-800'>Recommended Actions</p>
              {candidate.overallRisk === 'High' ? (
                <ul className='space-y-1.5 text-xs text-blue-700 list-disc list-inside'>
                  <li>Refer candidate to an Employee Assistance Programme (EAP) immediately.</li>
                  <li>Schedule a confidential conversation with HR or occupational health.</li>
                  <li>Consider role adjustments or flexible arrangements if proceeding.</li>
                  <li>Follow up with a qualified mental health professional within 48 hours.</li>
                </ul>
              ) : candidate.overallRisk === 'Moderate' ? (
                <ul className='space-y-1.5 text-xs text-blue-700 list-disc list-inside'>
                  <li>Offer access to counselling or EAP resources.</li>
                  <li>Monitor closely during onboarding and first 90 days.</li>
                  <li>Discuss support structures with candidate if they proceed.</li>
                </ul>
              ) : (
                <ul className='space-y-1.5 text-xs text-blue-700 list-disc list-inside'>
                  <li>No immediate action required.</li>
                  <li>Continue standard onboarding and wellbeing check-ins.</li>
                  <li>Encourage candidate to self-refer to EAP if needed in future.</li>
                </ul>
              )}
            </div>
          </div>

          {/* Export button */}
          <div className='flex justify-end'>
            <button
                type='button'
                onClick={downloadExport}
                className='inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700 transition-colors'
            >
              <DownloadIcon />
              Export Candidate Results
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Main: WellnessScreeningPage ──────────────────────────────────────────────

interface WellnessScreeningPageProps {
  summary: WellnessSummaryResult | null;
  candidateResults: WellnessCandidateResult[];
  isLoading: boolean;
  pagination: PaginationProps;
}

type PageView = 'overview' | 'detail';
type OverviewTab = 'summary' | 'candidates';

const downloadSummary = (summary: WellnessSummaryResult) => {
  const lines = ['Assessment,Completed,Pending', ...summary.assessments.map((a) => `${a.displayName},${a.completed},${a.pending}`)];
  const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = 'wellness_summary.csv'; a.click();
  URL.revokeObjectURL(url);
};

export const WellnessScreeningPage: React.FC<WellnessScreeningPageProps> = ({
  summary, candidateResults, isLoading, pagination,
}) => {
  const [view, setView]                               = useState<PageView>('overview');
  const [overviewTab, setOverviewTab]                 = useState<OverviewTab>('summary');
  const [detailCandidate, setDetailCandidate]         = useState<WellnessCandidateResult | null>(null);

  const handleView = (r: WellnessCandidateResult) => {
    setDetailCandidate(r);
    setView('detail');
  };

  const handleBack = () => {
    setView('overview');
    setDetailCandidate(null);
  };

  // ── Full candidate detail page ──────────────────────────────────────────────
  if (view === 'detail' && detailCandidate) {
    return (
        <CandidateDetailPage
            candidate={detailCandidate}
            allCandidates={candidateResults}
            onBack={handleBack}
            onSelectCandidate={setDetailCandidate}
        />
    );
  }

  // ── Overview page ──────────────────────────────────────────────────────────
  return (
    <div className='space-y-5'>

      {/* Breadcrumb */}
      <nav className='text-xs text-slate-400'>
        <span>Wellness</span>
        <span className='mx-1.5'>›</span>
        <span className='font-semibold text-slate-600'>Screening Results</span>
      </nav>

      {/* Header + export buttons */}
      <div className='flex items-start justify-between gap-4 flex-wrap'>
        <div>
          <h2 className='text-xl font-extrabold text-slate-900'>Wellness Screening</h2>
          <p className='text-sm text-slate-500'>Overview of candidate wellness screening results.</p>
        </div>
        <div className='flex items-center gap-2'>
          <button
              type='button'
              onClick={() => summary && downloadSummary(summary)}
              disabled={!summary}
              className='inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-40 transition-colors'
          >
            <DownloadIcon /> Export Summary
          </button>
          <button
              type='button'
              onClick={() => summary && downloadSummary(summary)}
              disabled={!summary}
              className='inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-40 transition-colors'
          >
            <DownloadIcon /> Export All Results
          </button>
        </div>
      </div>

      {/* Assessment overview cards */}
      <div>
        <h3 className='mb-3 text-sm font-bold text-slate-700'>Summary Overview</h3>
        <div className='flex flex-wrap gap-4'>
          {isLoading
            ? Array.from({ length: 3 }).map((_, i) => <SkeletonOverviewCard key={i} />)
            : summary
              ? summary.assessments.map((a) => <OverviewCard key={a.type} data={a} />)
              : <p className='text-sm text-slate-400'>No data available yet.</p>
          }
        </div>
      </div>

      {/* Sub-tabs: Summary | Candidate Results */}
      <div className='flex gap-1 border-b border-slate-100'>
        {([['summary', 'Summary'], ['candidates', 'Candidate Results']] as const).map(([key, label]) => (
          <button
              key={key}
              type='button'
              onClick={() => setOverviewTab(key)}
              className={`relative px-4 py-2 text-sm font-semibold transition-colors whitespace-nowrap ${
                overviewTab === key ? 'text-emerald-700' : 'text-slate-500 hover:text-slate-700'
              }`}
          >
            {label}
            {overviewTab === key && <span className='absolute bottom-0 left-0 right-0 h-[2px] bg-emerald-600 rounded-t-full' />}
          </button>
        ))}
      </div>

      {/* Summary tab content */}
      {overviewTab === 'summary' && (
        <div className='rounded-xl border border-slate-200 bg-white p-5'>
          <p className='text-sm text-slate-500 mb-4'>High-level overview of assessment completion and severity distribution.</p>
          {summary ? (
            <div className='space-y-3'>
              {summary.assessments.map((a) => {
                const total = a.bands.reduce((s, b) => s + b.count, 0);
                return (
                    <div key={a.type} className='flex items-center gap-4'>
                      <span className='w-16 text-sm font-semibold text-slate-700'>{a.displayName}</span>
                      <div className='flex-1 flex h-4 overflow-hidden rounded-full gap-0.5'>
                        {total > 0 && a.bands.map((band) => (
                            <div key={band.label}
                                 className={`h-4 rounded-full ${SEVERITY_BAND_COLOR_CLASS[band.label] ?? 'bg-slate-300'}`}
                                 style={{width: `${(band.count / total) * 100}%`}}
                                 title={`${band.label}: ${band.count}`}/>
                        ))}
                      </div>
                      <span className='text-xs text-slate-500 w-24 text-right'>{a.completed} completed</span>
                    </div>
                );
              })}
            </div>
          ) : (
              <p className='text-sm text-slate-400'>No summary data available yet.</p>
          )}
        </div>
      )}

      {/* Candidate Results tab content */}
      {overviewTab === 'candidates' && (
          <CandidatesResultsTable
              results={candidateResults}
              isLoading={isLoading}
              pagination={pagination}
              onView={handleView}
        />
      )}
    </div>
  );
};
